import { Document, Page, Text, View, Image, StyleSheet, Font, PDFDownloadLink } from "@react-pdf/renderer";

// Register font once
Font.register({
    family: "Poppins",
    src: "https://fonts.gstatic.com/s/poppins/v21/pxiEyp8kv8JHgFVrFJA.ttf",
});

// PDF Styles
const pdfStyles = StyleSheet.create({
    page: {
        flexDirection: "column",
        padding: 30,
        backgroundColor: "white",
        color: "#3C5063"
    },
    logo: {
        width: 100,
        position: "absolute",
        top: 30,
        right: 30,
    },
    title: { fontFamily: "Poppins", fontWeight: 900, fontSize: 10 },
    text: { fontFamily: "Poppins", fontWeight: 700, fontSize: 10 },
    detailsSection: {
        marginTop: 20,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginRight: 130,
    },
    roadmapContainer: {
        marginTop: 20,
        marginBottom: 100,
        paddingBottom: 50,
        gap: 5,
    },
    roadmapCont: {
        flexDirection: "row",
        gap: 10,
        marginBottom: 5,
    },
    listLabel: {
        fontWeight: 'bold',
        fontSize: 10,
    },
    listValue: {
        fontSize: 10,
    },
    indicatorRow: {
        flexDirection: 'row',
        justifyContent: 'flex-start',
        gap: 10,
        marginTop: 10,
    },
    indicatorGroup: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    greenIndicator: {
        width: 15,
        height: 10,
        backgroundColor: '#4CAF50',
        borderRadius: 6,
    },
    orangeIndicator: {
        width: 15,
        height: 10,
        backgroundColor: '#FF9800',
        borderRadius: 6,
    },
    whiteIndicator: {
        width: 15,
        height: 10,
        backgroundColor: '#FFFFFF',
        borderWidth: 1,
        borderColor: '#B0B0B0',
        borderRadius: 6,
    },
    indicatorText: {
        fontSize: 10,
    },
    studentInfo: {
        maxWidth: 50,
        width: "100%",
        display: "flex",
        justifyContent: "center",
    },
    studentName: {
        fontFamily: "Poppins",
        fontWeight: "normal",
        fontSize: 7.8,
        marginBottom: 1,
    },
    studentText: {
        fontFamily: "Poppins",
        fontSize: 7,
        marginBottom: 1,
    },
    stepsCont: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 5,
    },
    stepBox: {
        width: 55,
        minHeight: 70,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: "#6A4FA3",
        padding: 2,
        justifyContent: "center",
    },
    stepLevel: {
        fontSize: 6,
        fontFamily: "Poppins",
    },
    stepLabel: {
        fontSize: 8,
        lineHeight: 1.2,
        fontFamily: "Poppins",
        fontWeight: 'bold',
    },
    footer: {
        position: "absolute",
        bottom: 20,
        left: 30,
        right: 30,
    },
    footerText: { fontSize: 8, color: "#5c5c5c" },
});

// Document component
const PdfDocument = ({
    roadmaps,
    selectedSchool,
    currentTime,
    selectedInterventionTypes,
    selectedTutorGroup,
    selectedTutorGroupLead,
    selectedStudentStatuses,
    selectedYearGroup,
    includeNormalAttendance,
    includeLowAttendance
}) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            <Image style={pdfStyles.logo} src={require("../../assets/SCP-logo.jpg")} />

            {/* Header */}
            <View style={pdfStyles.detailsSection}>
                <Text style={pdfStyles.title}>Roadmaps Report</Text>
                <Text style={pdfStyles.text}>{currentTime}</Text>
            </View>

            {/* Details List */}
            <View style={{ marginTop: 10 }}>
                {selectedSchool?.name && (
                    <View style={{ flexDirection: "row", marginBottom: 2 }}>
                        <Text style={pdfStyles.listLabel}>School - </Text>
                        <Text style={pdfStyles.listValue}>{selectedSchool.name}</Text>
                    </View>
                )}
                {selectedTutorGroupLead?.name && (
                    <View style={{ flexDirection: "row", marginBottom: 2 }}>
                        <Text style={pdfStyles.listLabel}>Intervention lead - </Text>
                        <Text style={pdfStyles.listValue}>{selectedTutorGroupLead.name}</Text>
                    </View>
                )}
                {selectedTutorGroup?.name && (
                    <View style={{ flexDirection: "row", marginBottom: 2 }}>
                        <Text style={pdfStyles.listLabel}>Tutor Group - </Text>
                        <Text style={pdfStyles.listValue}>{selectedTutorGroup.name}</Text>
                    </View>
                )}
                {selectedYearGroup?.year && (
                    <View style={{ flexDirection: "row", marginBottom: 2 }}>
                        <Text style={pdfStyles.listLabel}>Year Group - </Text>
                        <Text style={pdfStyles.listValue}>{selectedYearGroup.year}</Text>
                    </View>
                )}
                {selectedStudentStatuses?.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 4 }}>
                        <Text style={pdfStyles.listLabel}>Student Statuses - </Text>
                        {selectedStudentStatuses.map((status, index) => (
                            <Text key={index} style={pdfStyles.listValue}>
                                {status.name}
                                {index !== selectedStudentStatuses.length - 1 ? ", " : ""}
                            </Text>
                        ))}
                    </View>
                )}
                {selectedInterventionTypes?.length > 0 && (
                    <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 4 }}>
                        <Text style={pdfStyles.listLabel}>Intervention Types - </Text>
                        {selectedInterventionTypes.map((type, index) => (
                            <Text key={index} style={pdfStyles.listValue}>
                                {type.name}
                                {index !== selectedInterventionTypes.length - 1 ? ", " : ""}
                            </Text>
                        ))}
                    </View>
                )}
                {(includeNormalAttendance || includeLowAttendance) && (
                    <View style={{ flexDirection: "row", marginBottom: 2 }}>
                        <Text style={pdfStyles.listLabel}>Attendance - </Text>
                        <Text style={pdfStyles.listValue}>
                            {includeNormalAttendance && includeLowAttendance
                                ? "Normal, Low"
                                : includeNormalAttendance
                                    ? "Normal"
                                    : "Low"}
                        </Text>
                    </View>
                )}
            </View>

            {/* Indicators */}
            <View style={pdfStyles.indicatorRow}>
                {/* Complete */}
                <View style={pdfStyles.indicatorGroup}>
                    <View style={pdfStyles.greenIndicator} />
                    <Text style={pdfStyles.indicatorText}>Complete</Text>
                </View>

                {/* In Progress */}
                <View style={pdfStyles.indicatorGroup}>
                    <View style={pdfStyles.orangeIndicator} />
                    <Text style={pdfStyles.indicatorText}>In Progress</Text>
                </View>

                {/* Upcoming */}
                <View style={pdfStyles.indicatorGroup}>
                    <View style={pdfStyles.whiteIndicator} />
                    <Text style={pdfStyles.indicatorText}>Upcoming</Text>
                </View>
            </View>

            {/* Roadmaps */}
            <View style={pdfStyles.roadmapContainer}>
                {roadmaps.map((roadmap, i) => (
                    <View style={pdfStyles.roadmapCont} key={`cont-${i}`} wrap={false}>
                        {/* Student Info */}
                        <View style={pdfStyles.studentInfo}>
                            <Text style={pdfStyles.studentName}>{roadmap?.pupil_info?.name}</Text>
                            <Text style={pdfStyles.studentText}>Year {roadmap?.pupil_info?.year}</Text>
                            <Text style={pdfStyles.studentText}>Ta: {roadmap?.pupil_info?.tutor}</Text>
                        </View>

                        {/* Steps */}
                        <View style={pdfStyles.stepsCont}>
                            {roadmap.roadmapSteps.map((step) => (
                                <View
                                    key={step?.id}
                                    style={[
                                        pdfStyles?.stepBox,
                                        step?.status === "complete"
                                            ? { backgroundColor: "#4CAF50", borderColor: "#4CAF50", color: "#fff" }
                                            : step?.status === "in-progress"
                                                ? { backgroundColor: "#FF9800", borderColor: "#FF9800", color: "#fff" }
                                                : {},
                                    ]}
                                >
                                    <Text style={pdfStyles.stepLevel}>
                                        Level {step?.level?.charAt(step?.level?.length - 1)}
                                    </Text>
                                    <Text style={pdfStyles.stepLabel}>{step?.label}</Text>
                                </View>
                            ))}
                        </View>
                    </View>
                ))}

                {/* Extra space so footer isn’t overlapped */}
                <View style={{ height: 50 }} />
            </View>

            {/* Footer */}
            <View style={pdfStyles.footer} fixed>
                <Text style={pdfStyles.footerText}>
                    School Consultancy Programme is part of Tutor Your Child Ltd (registered 13378546).
                    Assessments results are calculated by analysing the assessment information available
                    to us at the time using our experience and qualifications. This may not represent the
                    views of all agencies or individuals, and should be used in conjunction with other
                    information available.
                </Text>
            </View>
        </Page>
    </Document>
);

const RoadmapReportPDF = ({
    roadmaps,
    selectedSchool,
    currentTime,
    selectedInterventionTypes,
    selectedTutorGroup,
    selectedTutorGroupLead,
    selectedStudentStatuses,
    selectedYearGroup,
    includeNormalAttendance,
    includeLowAttendance
}) => {
    return (
        <PDFDownloadLink
            className="pdf-button"
            document={
                <PdfDocument
                    roadmaps={roadmaps}
                    selectedSchool={selectedSchool}
                    selectedTutorGroupLead={selectedTutorGroupLead}
                    selectedInterventionTypes={selectedInterventionTypes}
                    selectedTutorGroup={selectedTutorGroup}
                    selectedStudentStatuses={selectedStudentStatuses}
                    selectedYearGroup={selectedYearGroup}
                    currentTime={currentTime}
                    includeLowAttendance={includeLowAttendance}
                    includeNormalAttendance={includeNormalAttendance}
                />
            }
            fileName={`${selectedSchool?.name || ""} Roadmap Report.pdf`}
        >
            {({ loading }) => (loading ? "Generating PDF..." : "Download PDF")}
        </PDFDownloadLink>
    );
};

export default RoadmapReportPDF;
